/**
 * Custom Login Page
 * Auto-redirects to Auth0 instead of showing a form
 */

import { useEffect, useRef } from 'react';
import { useLogin } from 'react-admin';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { darkColors, common } from '@rekindle/shared/theme';
import { logger } from '../utils/logger';

export function LoginPage() {
  const login = useLogin();
  const hasRedirected = useRef(false);
  const { search } = useLocation();
  const accessDenied = new URLSearchParams(search).get('reason') === 'access-denied';

  useEffect(() => {
    logger.info('LoginPage mounted');
    if (accessDenied) return;

    // Only redirect once
    if (hasRedirected.current) {
      logger.info('Already redirected, skipping');
      return;
    }

    // Check if we're handling a callback (has code in URL)
    const params = new URLSearchParams(window.location.search);
    if (params.has('code') && params.has('state')) {
      logger.info('Callback detected, skipping redirect');
      return;
    }

    hasRedirected.current = true;
    logger.info('Triggering Auth0 redirect...');

    // Call login - note: this promise never resolves because browser redirects away
    // Only the catch handler matters (for errors before redirect starts)
    login({}).catch((error) => {
      logger.error('Login redirect failed:', error);
      // Reset flag to allow retry
      hasRedirected.current = false;
    });
  }, [login, accessDenied]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{
        background: `linear-gradient(135deg, ${darkColors.background} 0%, ${darkColors.surface} 100%)`,
      }}
    >
      {!accessDenied && <CircularProgress sx={{ color: common.white, mb: 2 }} />}
      <Typography color={common.white} variant="h6">
        {accessDenied ? 'Administrator access is required' : 'Redirecting to login...'}
      </Typography>
      <Typography color={darkColors.textSecondary} variant="body2" sx={{ mt: 1 }}>
        {accessDenied ? 'This account cannot open the admin panel. Sign in with an administrator account to continue.' : 'ReKindle Admin Panel'}
      </Typography>
      {accessDenied && <Button variant="contained" sx={{ mt: 3 }} onClick={() => {
        login({ forceLogin: true }).catch(error => logger.error('Login redirect failed:', error));
      }}>Sign in with another account</Button>}
    </Box>
  );
}
